import { useId, useState } from 'react'
import { Copy, RefreshCw, UserPlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { canSeeMoney, type RoleCode } from '@cattle/money'
import type { CreatePersonRequest } from '@cattle/contracts'

import {
  Avatar,
  Button,
  DataTable,
  Field,
  LoadingSkeleton,
  Modal,
  PageHeader,
  QueryBoundary,
  RadioCards,
  Section,
  StatusBadge,
  TextInput,
  useToast,
  type Column,
  type RadioCardOption,
  type StatusTone,
} from '../../../components'
import { queryKeys, usePeople, type Person } from '../../../fixtures'
import { post } from '../../../lib/apiClient'
import { t } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'
import { routes } from '../../../lib/routes'

/**
 * O-03 · Add a person.
 *
 * The two roles sit SIDE BY SIDE so they are compared rather than remembered —
 * the phone could not fit this, and the choice decides whether a person ever
 * sees a price. The save button names both the person and the role, so the last
 * thing read before committing is the decision being made.
 *
 * THE PASSWORD IS SHOWN ONCE, AND THIS SCREEN IS THE ONLY TIME.
 *
 * The code below is generated here and SENT as `password`, which is what makes
 * the deck's "generate another" button mean anything. The server stores an
 * argon2 hash of it and sets `mustChangePassword`; no route returns it again,
 * from either side. The reveal after a successful save therefore repeats it
 * with a copy button and says it cannot be shown a second time — leaving the
 * screen without copying it means it has to be reset.
 */

const ROLE_TONE: Record<RoleCode, StatusTone> = {
  OWNER: 'ok',
  PARTNER: 'warn',
  STAFF: 'neutral',
}

/** A new account's role is one of two. OWNER is not on offer from this screen. */
type NewRole = Extract<RoleCode, 'PARTNER' | 'STAFF'>

/** No O/0 and no I/1: this code is read out loud down a phone line. */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const PASSWORD_LENGTH = 8

function generatePassword(): string {
  let code = ''
  for (let index = 0; index < PASSWORD_LENGTH; index += 1) {
    code += ALPHABET.charAt(Math.floor(Math.random() * ALPHABET.length))
  }
  return code
}

/**
 * CONTRACT GAP: `personSchema` has no `oneTimePassword` field, even though
 * `createPersonRequest.password` promises one is "returned once by
 * `oneTimePassword`" when the password is omitted. The server sends it as an
 * extra key, so the response is read RAW here — a strict parse against the
 * contract would strip precisely the field the reveal exists for.
 */
type CreatedPerson = { id: string; name: string; oneTimePassword?: string }

export function NewPersonScreen() {
  const people = usePeople()
  const toast = useToast()
  const navigate = useNavigate()
  const nameId = useId()
  const usernameId = useId()

  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<NewRole>('PARTNER')
  const [password, setPassword] = useState(generatePassword)
  /** Set once the server has accepted the person. Nothing can fetch it back. */
  const [added, setAdded] = useState<{ name: string; password: string } | null>(null)

  const ready = name.trim().length > 0 && username.trim().length > 0

  const create = useApiMutation<void, CreatedPerson>({
    run: async () => {
      const body: CreatePersonRequest = {
        name: name.trim(),
        username: username.trim(),
        role,
        password,
      }
      return post<CreatedPerson>('/people', body)
    },
    // A new account changes the people list and the count on the overview.
    invalidates: [queryKeys.people, queryKeys.ownerOverview],
    success: (result) => t('o.newPerson.added', { name: result.name }),
    onDone: (result) =>
      // `oneTimePassword` only appears when the server generated one. It does
      // not here — the password above was sent — but it is preferred anyway, so
      // this reveal shows what the server actually set rather than what this
      // screen assumed.
      setAdded({ name: result.name, password: result.oneTimePassword ?? password }),
  })

  const roleOptions: RadioCardOption<NewRole>[] = [
    {
      value: 'PARTNER',
      title: t('role.PARTNER'),
      tag: t('o.newPerson.partnerTag'),
      points: [
        { text: t('o.newPerson.partner1'), allowed: true },
        { text: t('o.newPerson.partner2'), allowed: true },
        { text: t('o.newPerson.partner3'), allowed: false },
        { text: t('o.newPerson.partner4'), allowed: false },
      ],
    },
    {
      value: 'STAFF',
      title: t('role.STAFF'),
      tag: t('o.newPerson.staffTag'),
      points: [
        { text: t('o.newPerson.staff1'), allowed: true },
        { text: t('o.newPerson.staff2'), allowed: true },
        { text: t('o.newPerson.staff3'), allowed: false },
        { text: t('o.newPerson.staff4'), allowed: false },
      ],
    },
  ]

  /** The deck's context table: the list this person is about to be added to. */
  const columns: Column<Person>[] = [
    {
      id: 'name',
      header: t('label.name'),
      cell: (person) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={person.name} size="sm" />
          <span className="font-semibold text-dark">{person.name}</span>
        </div>
      ),
    },
    {
      id: 'username',
      header: t('label.username'),
      cell: (person) => <span className="tnum text-sub">{person.username}</span>,
    },
    {
      id: 'role',
      header: t('o.people.colCanDo'),
      cell: (person) => (
        <StatusBadge tone={ROLE_TONE[person.role]}>{t(`role.${person.role}`)}</StatusBadge>
      ),
    },
    {
      id: 'seesMoney',
      header: t('o.people.colSeesMoney'),
      cell: (person) =>
        canSeeMoney(person.role) ? (
          <StatusBadge tone="ok">{t('label.yes')}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t('label.no')}</StatusBadge>
        ),
    },
  ]

  const close = () => navigate(routes.owner.people())

  const copyPassword = (value: string) => {
    void navigator.clipboard.writeText(value).then(
      () => toast.show(t('act.copied')),
      () => toast.show(t('o.newPerson.shownOnce'), 'danger'),
    )
  }

  return (
    <>
      <PageHeader
        title={t('o.people.title')}
        subtitle={t('o.people.subtitle', { count: people.data?.length ?? 0 })}
      />

      <Section>
        <QueryBoundary query={people} skeleton={<LoadingSkeleton variant="table" rows={3} />}>
          {(rows) => (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(person) => person.id}
              caption={t('o.people.title')}
            />
          )}
        </QueryBoundary>
      </Section>

      {/* The form closes the moment the server accepts, and the reveal takes
          its place — the two are never on screen together. */}
      <Modal
        open={added === null}
        width="lg"
        onClose={close}
        title={t('o.newPerson.title')}
        subtitle={t('o.newPerson.subtitle')}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              icon={<UserPlus className="size-4" />}
              disabled={!ready || create.isPending}
              onClick={() => create.mutate()}
            >
              {ready
                ? t('o.newPerson.submit', { name: name.trim(), role: t(`role.${role}`) })
                : t('o.newPerson.submitEmpty')}
            </Button>
          </>
        }
      >
        <div className="grid gap-x-3.5 md:grid-cols-2">
          <Field required label={t('o.newPerson.theirName')} htmlFor={nameId}>
            <TextInput
              id={nameId}
              value={name}
              onChange={setName}
              className="max-w-none"
            />
          </Field>
          <Field required label={t('label.username')} htmlFor={usernameId}>
            <TextInput
              id={usernameId}
              value={username}
              onChange={setUsername}
              className="max-w-none"
            />
          </Field>
        </div>

        <div className="mt-1.5 mb-2 text-[13px] font-semibold text-dark">
          {t('o.newPerson.whatTheyCanDo')}
          <span className="text-danger"> *</span>
        </div>
        <RadioCards
          name="new-person-role"
          options={roleOptions}
          value={role}
          onChange={setRole}
        />

        <Field
          layout="row"
          className="mt-3.5 border-t border-muted/15"
          label={t('o.newPerson.firstPassword')}
          hint={t('o.newPerson.firstPasswordHint')}
        >
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="flex h-10 items-center rounded-[10px] border border-glass-border bg-glass px-3.5 font-display text-base font-bold tracking-[0.10em] tnum text-dark">
              {password}
            </span>
            <Button
              size="sm"
              icon={<RefreshCw className="size-3.5" />}
              onClick={() => setPassword(generatePassword())}
            >
              {t('o.newPerson.regenerate')}
            </Button>
            <Button
              size="sm"
              icon={<Copy className="size-3.5" />}
              onClick={() => copyPassword(password)}
            >
              {t('act.copy')}
            </Button>
          </div>
        </Field>

        <p className="mt-2.5 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('o.newPerson.shownOnce')}
        </p>
      </Modal>

      <Modal
        open={added !== null}
        onClose={close}
        title={t('o.newPerson.addedTitle', { name: added?.name ?? '' })}
        footer={
          <Button variant="primary" onClick={close}>
            {t('o.newPerson.done')}
          </Button>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-sub">{t('o.newPerson.addedBody')}</p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 text-[13px] font-semibold text-dark">{t('label.username')}</div>
            <span className="flex h-11 items-center rounded-[10px] border border-glass-border bg-glass px-4 font-display text-base font-bold tnum text-dark">
              {username.trim()}
            </span>
          </div>
          <div>
            <div className="mb-1.5 text-[13px] font-semibold text-dark">
              {t('o.newPerson.theirPassword')}
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="flex h-11 items-center rounded-[10px] border border-glass-border bg-glass px-4 font-display text-lg font-bold tracking-[0.10em] tnum text-dark">
                {added?.password}
              </span>
              <Button
                icon={<Copy className="size-3.5" />}
                onClick={() => copyPassword(added?.password ?? '')}
              >
                {t('act.copy')}
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('o.newPerson.shownOnce')}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-muted">{t('o.people.mustChange')}</p>
      </Modal>
    </>
  )
}
